import { Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { UserRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";
import { listToString } from "@nens/shared-utils";

import { cancelButtonText } from "@/translations";

import { useUpdateUsers } from "../api/update-tenant-user";

interface TenantUserActivateModalProps {
  users: UserRead[];
  onClose: () => void;
}

export function TenantUserActivateModal({ users, onClose }: TenantUserActivateModalProps) {
  const intl = useIntl();
  const { mutate, isMutating, isError } = useUpdateUsers(users.map(({ id }) => id));
  const multiple = users.length > 1;

  const activateUser = () => {
    mutate({ active: true }, { onSuccess: onClose });
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <>
          <FormattedMessage id="header.activate_user" defaultMessage="Activate user account" />
          {multiple && "s"}
        </>
      }
      maxW="xl"
      h="sm"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: intl.formatMessage(
            {
              id: "button.activate_user",
              defaultMessage: "Activate user account{multiple, select, true {s} other {}}",
            },
            { multiple }
          ),
          variant: "primary",
          isDisabled: isMutating,
          onClick: activateUser,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          <FormattedMessage
            id="text.user_email"
            defaultMessage="Email address{multiple, select, true {es} other {}}: "
            values={{ multiple }}
          />
          <Text as="span" color="brand.deep_sea" fontWeight="semibold">
            {listToString(users.map(({ email }) => email))}
          </Text>
        </Text>
        <Text>
          {multiple ? (
            "These user accounts have previously been deactivated. By activating them, if login credentials are still valid, the users can log in again and have their previous roles restored."
          ) : (
            <FormattedMessage
              id="paragraph.activate_user"
              defaultMessage="This user account has previously been deactivated. By activating it, if login credentials are still valid, the user can log in again and have their previous roles restored."
            />
          )}
        </Text>
        {/* FUTURE: This option is not working yet.
        <Checkbox>
          <FormattedMessage
            id="checkbox.send_activation_mail"
            defaultMessage="Send the user{multiple, select, true {s} other {}} an email about account activation"
            values={{ multiple }}
          />
        </Checkbox> */}
        {isMutating && <RanaLoading size="md" isCentered />}
        {isError && (
          <Text color="red.500">
            <FormattedMessage
              id="error.activate_user_failed"
              defaultMessage="Failed to activate user{multiple, select, true {s} other {}} account. Please try again."
              values={{ multiple }}
            />
          </Text>
        )}
      </Flex>
    </RanaModal>
  );
}
